import type { ActionType, RiskType } from '@/domains/academicRisk';
import type { CharacterizationApiResponse } from '@/domains/characterization/characterization.entity';
import { create } from 'zustand';
import type {
  CharacterizationStoreEntity,
  CharacterizationStoreRepository,
} from '../models';

/**
 * Valores por defecto del store
 */
const defaultCharacterizationState: CharacterizationStoreEntity = {
  apiData: null,
  isLoading: false,
  actionTypes: [],
  riskTypes: [],
  catalogsLoaded: false,
};

/**
 * Store de Zustand para vista de Caracterización
 * Gestiona datos del estudiante individual y su información de riesgo
 * No persiste datos (información sensible y específica por sesión)
 */
export const useCharacterizationStore =
  create<CharacterizationStoreRepository>()((set, get) => ({
    ...defaultCharacterizationState,

    /**
     * Carga datos de caracterización desde la API
     * Previene actualizaciones innecesarias comparando con datos existentes
     *
     * @param apiResponse - Respuesta de la API con datos del estudiante
     */
    loadData: (apiResponse: CharacterizationApiResponse) => {
      const { apiData } = get();
      if (JSON.stringify(apiData) === JSON.stringify(apiResponse)) return;

      set({
        apiData: apiResponse,
        isLoading: false,
      });
    },

    loadActionTypes: (types: ActionType[]) => {
      set({
        actionTypes: types,
        catalogsLoaded: get().riskTypes.length > 0,
      });
    },

    loadRiskTypes: (types: RiskType[]) => {
      set({
        riskTypes: types,
        catalogsLoaded: get().actionTypes.length > 0,
      });
    },

    /**
     * Controla el estado de carga de datos
     *
     * @param loading - true cuando se están cargando datos
     */
    setLoading: (loading: boolean) => set({ isLoading: loading }),

    /**
     * Resetea el store a su estado inicial
     * Limpia datos de caracterización
     */
    clearData: () => set({ ...defaultCharacterizationState }),
  }));
